/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LuiCard,
  LuiMaterialTable,
  LuiSelect,
  LuiToolbar,
  LuiPageHeader,
  LuiEmptyState,
  LuiMaterialTableHeadCellStyles
} from '@jda/lui-common-component-library-mui5';
import { AlertCircle } from '@jda/lui-common-icon-library-mui5';
import { Backdrop, Box, Button, CircularProgress, Typography, TableCellProps } from '@mui/material';
import CustomTablePagination from '../../shared/CustomTablePagination';
import DetailsModal from '../../shared/Dialog/DetailsModal';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { addMachineSubscriptions, getClientById, getModuleApis, getRealmEntitlements } from '../../services/client';
import { setSelectedTab } from '../../store/appLoader/appLoaderActionCreator';
import {
  setCurrentMachineName,
  setCurrentMachineSelection,
  setMachineSelectedTab
} from '../../store/machine/machineActionCreator';
import { selectMachineCurrentSelection } from '../../store/machine/machineSelector';
import { displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { IClient } from '../../models/Machines/ClientContracts';
import interpolateString from '../../utility/stringInterpolation';
import { useStyles } from './styles';
import {
  ADD_SUBSCRIPTIONS,
  API_LIST,
  DROPDOWN_WIDTH,
  DROPDOWN_MAX_HEIGHT,
  EMPTY_HEADER,
  NAME,
  VERSION,
  ROWS_PER_PAGE_LABEL,
  UNSUCCESSFUL_SUBSCRIPTION_TITLE,
  UNSUCCESSFUL_SUBSCRIPTION_MESSAGE,
  ERROR_MACHINE_NOT_FOUND,
  API_SUBSCRIBED_SUCCESSFULLY,
  ERROR_WHILE_FETCHING_DATA,
  SUBSCRIBE,
  SELECTED_OF,
  URI,
  RESTRICTION_MESSAGE_NEW
} from './constants';
import { API_SUBSCRIPTIONS, LABELS } from '../../components/MachineDetails/constants';
import { MACHINE_TYPES } from '../../components/MachineList/Constants';
import { selectRealmInfo } from '../../store/realmSettings/realmSettingsSelector';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';

const INITIAL_PAGE_NUMBER = 0;
const INITIAL_PAGE_SIZE = 20;

const columns = [
  {
    header: NAME,
    accessorFn: (row) => row.name || row.displayName,
    id: 'name',
    muiTableHeadCellProps: ({ column, table }) =>
    ({
      sx: theme => ({
        ...LuiMaterialTableHeadCellStyles({
          column,
          theme,
          table,
          styling: 'standard',
          virtualizationEnabled: false
        }),
        width: '45%'
      })
    } as TableCellProps)
  },
  {
    header: VERSION,
    accessorKey: 'version',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '10%'
        })
      } as TableCellProps)
  },
  {
    header: URI,
    accessorKey: 'uri',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '45%'
        })
      } as TableCellProps)
  }
];

const AddSubscriptions = () => {
  const { machineId } = useParams<{ machineId: string }>();

  const [machine, setMachine] = useState<IClient>();
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(INITIAL_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [response, setResponse] = useState<any>();
  const [selectedApis, setSelectedApis] = useState<any[]>([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [erroredApis, setErroredApis] = useState<any[]>([]);
  const selectedMachine = useSelector(selectMachineCurrentSelection);

  const dispatch = useDispatch();
  const history = useHistory();
  const { height } = useWindowDimensions();
  const { classes } = useStyles();
  const selectedRealmInfo = useSelector(selectRealmInfo);

  const moduleData = useMemo(
    () => modules.map(module => ({ ...module, key: module.id, displayName: module.nameWithKey })),
    [modules]
  );

  const selectedModule = useMemo(() => moduleData.find(module => module.id === selectedModuleId), [
    moduleData,
    selectedModuleId
  ]);

  useEffect(() => {
    getClientById(machineId)
      .then(machine => {
        setMachine(machine);
      })
      .catch(error => {
        history.replace('/');
        dispatch(displaySnackbar(`${ERROR_MACHINE_NOT_FOUND}: ${error}`, 'error'));
      });
  }, [machineId, history, dispatch]);

  const fetchModuleApis = useCallback(
    async (module: any, itemsPerPage: number, page: number) => {
      const machineType = selectedMachine.isExternal ? 'external' : 'internal';
      setSelectedModuleId(module.id);
      setPageSize(itemsPerPage);
      setPageNumber(page);
      const moduleKey = module.moduleKey || module.name;
      try {
        const response = await getModuleApis(moduleKey, { page, pageSize: itemsPerPage, apiType: machineType });
        const updatedResponse = response.data.map((api: any, i: number) => {
          return {
            ...api,
            id: `${api.name}-${api.version}-${i}`
          };
        });
        setResponse({
          ...response,
          data: updatedResponse
        });
      } catch (error: any) {
        const errMessage = error.response?.data?.message || error.message;
        dispatch(displaySnackbar(interpolateString([errMessage], ERROR_WHILE_FETCHING_DATA), 'error'));
        setResponse({ data: [], metadata: { totalCount: 0 } });
      }
    },
    [dispatch]
  );

  useEffect(() => {
    // const machineType = selectedMachine.isExternal ? 'external' : 'internal';
    getRealmEntitlements()
      .then(({ modules }:any) => {
        // if (machineType === 'external') {
          modules = modules.map((module: any) => ({ ...module, nameWithKey: `${module.name} (${module.moduleKey})` }));
        // } else {
        //   modules = modules.map((module: any, index: number) => ({
        //     id: index + 1,
        //     name: module.toUpperCase(),
        //     moduleKey: module.toLowerCase(),
        //     nameWithKey: `${module.toUpperCase()}`
        //   }));
        // }
        setModules(modules);
        fetchModuleApis(modules[0], INITIAL_PAGE_SIZE, INITIAL_PAGE_NUMBER);
      })
      .catch((error: any) => {
        const errMessage = error.response?.data?.message || error.message;
        dispatch(displaySnackbar(interpolateString([errMessage], ERROR_WHILE_FETCHING_DATA), 'error'));
        setResponse({ data: [], metadata: { totalCount: 0 } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchModuleApis]);

  const handleBackClick = () => {
    dispatch(setCurrentMachineSelection(machine!));
    dispatch(setCurrentMachineName(machine!.displayName));
    if (history.location.search) {
      history.push(`/machineDetail/${machineId}${history.location.search}`, { fromPage: 'add-subscriptions' });
    } else {
      history.push('/machineDetail/' + machineId, { fromPage: 'add-subscriptions' });
    }
  };

  const handleSaveClick = async () => {
    setIsRequesting(true);

    const promiseResults = await Promise.all(
      selectedApis.map(async api => {
        try {
          await addMachineSubscriptions(machineId, [
            {
              name: api.name,
              version: api.version,
              moduleKey: selectedModule.moduleKey || selectedModule.name.toLowerCase()
            }
          ]);
        } catch {
          return api;
        }
      })
    );

    const erroredApis = promiseResults.filter(result => result !== undefined);

    if (erroredApis.length > 0) {
      setErroredApis(erroredApis);
      setIsRequesting(false);
      return;
    }

    dispatch(setMachineSelectedTab(API_SUBSCRIPTIONS));
    dispatch(setSelectedTab('Machines'));
    dispatch(setCurrentMachineSelection(machine!));
    dispatch(setCurrentMachineName(machine!.displayName));
    dispatch(displaySnackbar(API_SUBSCRIBED_SUCCESSFULLY, 'success'));
    if (history.location.search) {
      history.push(`/machineDetail/${machineId}${history.location.search}`, { fromPage: 'add-subscriptions' });
    } else {
      history.push('/machineDetail/' + machineId, { fromPage: 'add-subscriptions' });
    }
  };

  const handleModuleSelectionChange = (event: any) => {
    setSelectedApis([]);
    fetchModuleApis(event.target.value, pageSize, 0);
  };

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    fetchModuleApis(selectedModule, itemsPerPage, page - 1);
  };

  const handleErroredApisModalClose = () => {
    setErroredApis([]);
  };
  const getMachineSubtitle = () => {
    let subtitle = '';
    if (machine?.displayName) {
      subtitle += `Machine: ${machine.displayName} | `;
    }
    if (machine?.type) {
      subtitle += `Type: ${MACHINE_TYPES[machine.type] || ''}`;
    }
    if (
      machine?.isExternal !== undefined &&
      selectedRealmInfo.envType === 'admin' &&
      machine?.type === 'apim'
    ) {
      subtitle += machine?.isExternal === false ? ` | Category: ${LABELS.INTERNAL}` : ` | Category: ${LABELS.EXTERNAL}`;
    }
    return subtitle;
  };
  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <div>
          <LuiSelect
            itemData={moduleData}
            selectedItem={selectedModule}
            placeholder="Select Module"
            inputLabel=""
            width={DROPDOWN_WIDTH}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            onSelectionChange={handleModuleSelectionChange}
          />
        </div>
      )
    };
    const rightComponents = {
      component: (
        <div>
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </div>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState imageItem={null} heading={EMPTY_HEADER} />
      </Box>
    );
  };
  return (
    <Box className={classes.root}>
      <LuiPageHeader
        title={ADD_SUBSCRIPTIONS}
        subtitle={getMachineSubtitle()}
        isCollapsable={false}
        onClickBack={handleBackClick}
        toolBarSlot={[
          {
            alwaysVisible: true,
            render: (
              <Button
                variant="outlined"
                onClick={handleSaveClick}
                className={classes.actionButton}
              // disabled={selectedApis.length === 0 || selectedApis.length > MAX_APIS_SELECTION}
              >
                {SUBSCRIBE}
              </Button>
            )
          }
        ]}
      ></LuiPageHeader>
      {isRequesting && (
        <Backdrop open={true}>
          <CircularProgress color="inherit" size={44} />
        </Backdrop>
      )}
      <LuiCard
        headerBordered={true}
        hasCardContentPadding={false}
        titleVariant="body1"
        title={`${API_LIST} ${selectedApis.length > 0 ? `(${selectedApis.length} ${SELECTED_OF} ${response?.metadata?.totalCount})` : ''
          }`}
      >
        <CustomTablePagination
          startsFromZero={true}
          data={response?.data ?? []}
          loading={!response}
          withoutPagination={!response}
          totalCount={response?.metadata?.totalCount ?? 0}
          columns={columns}
          selectedRowsData={selectedApis}
          onSelectedRowsDataChange={setSelectedApis}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage={ROWS_PER_PAGE_LABEL}
          currentPageNumber={pageNumber}
          currentItemsPerPage={pageSize}
          onChangePageOptions={handleChangePageOptions}
          maxTableBodyHeight={height - 35 * 8}
          renderTopToolbar={extendedToolbar}
          renderEmptyRowsFallback={emptyTableFallback}
          additionalContent={
            response ? (
              <Box className={classes.alertMsg}>
                <AlertCircle className={classes.warningIcon} />
                <Typography variant="body1">{RESTRICTION_MESSAGE_NEW}</Typography>
              </Box>
            ) : null
          }
        />
      </LuiCard>
      {erroredApis.length > 0 && (
        <DetailsModal
          openModal={true}
          handleClose={handleErroredApisModalClose}
          dialogTitle={UNSUCCESSFUL_SUBSCRIPTION_TITLE}
        >
          <Typography>{UNSUCCESSFUL_SUBSCRIPTION_MESSAGE}</Typography>
          <LuiMaterialTable data={erroredApis} keyAccessor="id" columns={columns} />
        </DetailsModal>
      )}
      <SnackbarAlert />
    </Box>
  );
};

export default AddSubscriptions;
